import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box } from '@mui/system';

import { Button } from '@mui/material';
import { OfferProgramDto } from '../Types/offerProgramTypes';
import { useUpdateOfferProgramMutation } from '../Service/offerProgramApi';
import { useGetOfferProgramsForOfferQuery } from '../Service/extendedOfferProgramApi';
import DataTable, { ColumnConfig, ColumnType } from '../../Shared/Components/DataTable';
import { BookingsRoutes } from '../../../Router/path';
import SaveAsTemplateDialog from '../../Offers/Components/SaveAsTemplateDialog/SaveAsTemplateDialog';
import AutomaticOfferProgramDialog from './AutomaticOfferProgramDialog';

interface OfferProgramTableProps {
  offerId: number;
}

const OfferProgramTable = ({ offerId }: OfferProgramTableProps) => {
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedRow, setEditedRow] = useState<Partial<OfferProgramDto>>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([]);
  const navigate = useNavigate();
  const [updateOfferProgram] = useUpdateOfferProgramMutation();
  const { data, isLoading, isError, error, refetch } = useGetOfferProgramsForOfferQuery(offerId);

  const [openSaveAsTemplateDialog, setOpenSaveAsTemplateDialog] = useState(false);
  const [openGenerateProgramDialog, setOpenGenerateProgramDialog] = useState(false);

  const errorStatus = (error as any)?.status;
  const isNotFound = errorStatus === 404;
  const shouldShowError = isError && !isNotFound;
  const normalizedData = isNotFound ? [] : (data ?? []);

  const apiErrorMessage =
    (error as any)?.data?.message ||
    (typeof (error as any)?.data === 'string' ? (error as any).data : undefined);

  const fallbackErrorMessage = (() => {
    if (errorStatus === 401) {
      return 'Brak autoryzacji. Zaloguj się ponownie i spróbuj jeszcze raz.';
    }

    if (errorStatus === 403) {
      return 'Brak uprawnień do odczytu programu oferty.';
    }

    if (typeof errorStatus === 'number' && errorStatus >= 500) {
      return 'Wystąpił błąd serwera podczas pobierania programu oferty. Spróbuj ponownie za chwilę.';
    }

    if (errorStatus === 'FETCH_ERROR') {
      return 'Nie udało się połączyć z serwerem. Sprawdź połączenie i spróbuj ponownie.';
    }

    return 'Nie udało się pobrać programu oferty.';
  })();

  const resolvedErrorMessage = apiErrorMessage || fallbackErrorMessage;

  const columns: ColumnConfig<OfferProgramDto>[] = [
    {
      key: 'dayFrom',
      label: 'Dzień od',
      editable: true,
      type: ColumnType.number,
    },
    {
      key: 'dayTo',
      label: 'Dzień do',
      editable: true,
      type: ColumnType.number,
    },
    {
      key: 'description',
      label: 'Opis dnia/bloku',
      editable: true,
      type: ColumnType.wysiwyg,

      render: (row: OfferProgramDto): React.ReactNode => (
        <span
          dangerouslySetInnerHTML={{
            __html: row.description ?? '',
          }}
        />
      ),
    },
  ];
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        {normalizedData.length === 0 && (
          <Button variant="contained" onClick={() => setOpenGenerateProgramDialog(true)}>
            Stwórz program oferty
          </Button>
        )}

        {normalizedData.length > 0 && (
          <Button variant="outlined" onClick={() => setOpenSaveAsTemplateDialog(true)}>
            Zapisz program jako szablon
          </Button>
        )}
      </Box>
      <Box sx={{ mt: 3 }}>
        <DataTable
          columns={columns}
          data={normalizedData}
          isLoading={isLoading}
          isError={shouldShowError}
          errorMessage={shouldShowError ? resolvedErrorMessage : undefined}
          emptyMessage={
            'Program oferty nie został jeszcze utworzony. Użyj przycisku „Stwórz program oferty”.'
          }
          getRowId={(row) => row.id}
          onEdit={(id) => navigate(BookingsRoutes.update.replace(':bookingId', id.toString()))}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(newSize) => {
            setRowsPerPage(newSize);
            setPage(0);
          }}
          selected={selectedBookingIds}
          onSelectionChange={setSelectedBookingIds}
          editingRowId={editingRowId}
          setEditingRowId={setEditingRowId}
          editedRow={editedRow}
          setEditedRow={setEditedRow}
          onInlineSave={async (updatedRow) => {
            await updateOfferProgram({ id: updatedRow.id!, data: updatedRow }).unwrap();
            setEditingRowId(null);
            setEditedRow({});
          }}
        />
      </Box>

      <AutomaticOfferProgramDialog
        offerId={offerId}
        open={openGenerateProgramDialog}
        onClose={() => setOpenGenerateProgramDialog(false)}
        onSuccess={() => {
          refetch();
        }}
      />

      <SaveAsTemplateDialog
        open={openSaveAsTemplateDialog}
        onClose={() => setOpenSaveAsTemplateDialog(false)}
        blocks={normalizedData} // <-- przekazujesz aktualne bloki programu
        onSuccess={() => {
          // np. toast, odświeżenie listy szablonów itp.
        }}
      />
    </>
  );
};

export default OfferProgramTable;
